/**
 * Event Preview Component
 * 
 * Displays preview of events to be created in bulk operation.
 * Shows total count and list of events with names and dates.
 * 
 * Requirements: 5.1-5.5
 */

import { Modal } from '../../common/Modal';
import { Card } from '../../common/Card';
import { Button } from '../../common/Button';
import type { CreateEventDto, Location } from '../../../api/rest-client';

export interface EventPreviewProps {
  events: CreateEventDto[];
  locations: Location[];
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

/**
 * Format ISO datetime string for display
 */
function formatEventDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Event Preview Component
 * Requirements: 5.1-5.5
 */
export function EventPreview({
  events,
  locations,
  isOpen,
  onClose,
  onConfirm,
}: EventPreviewProps) {
  /**
   * Get location name from location_id
   */
  const getLocationName = (locationId: string): string => {
    const location = locations.find(loc => loc.location_id === locationId);
    return location ? location.name : 'Unknown Location';
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Preview Events"
      className="max-w-2xl"
    >
      <div className="space-y-4">
        {/* Summary */}
        <Card className="bg-primary-50 border border-primary-200">
          <div className="text-center">
            <div className="text-3xl font-bold text-primary-600">
              {events.length}
            </div>
            <div className="text-sm text-gray-600">
              {events.length === 1 ? 'event' : 'events'} will be created
            </div>
          </div>
        </Card>
        
        {/* Event List */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Events to Create
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {events.map((event, index) => (
              <Card key={index} className="bg-gray-50">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">
                      {event.event_name}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {formatEventDate(event.event_starttime)}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      Type: {event.event_type}
                    </div>
                    <div className="text-sm text-gray-500">
                      Location: {getLocationName(event.event_location)}
                    </div>
                  </div>
                  <div className="text-sm text-gray-500">
                    #{index + 1}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm}>
            Proceed to Create
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default EventPreview;
